import { A } from '@ember/array';
import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { isBlank } from '@ember/utils';
import type { AsyncHasMany } from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { timeout, task } from 'ember-concurrency';
import HandsonTable from 'garaje/components/handson-table';
import DropdownOption from 'garaje/models/dropdown-option';
import type FlowModel from 'garaje/models/flow';
import type GroupInviteInstanceTemplate from 'garaje/models/group-invite-instance-template';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import { convertToISO } from 'garaje/utils/date-format';
import { DateFieldFormat, SignInFieldKind } from 'garaje/utils/enums';
import {
  type ConditionalDependency,
  createConditionalPresenceValidator,
  createDateFieldValidator,
  escapeHTML,
  extractConditionalDependencies,
} from 'garaje/utils/spreadsheet-validation';
import zft from 'garaje/utils/zero-for-tests';
import type Handsontable from 'handsontable';
import type { GridSettings } from 'handsontable';
import { filterBy, reads } from 'macro-decorators';

export interface InvitesSpreadsheetOutputArgs {
  csvHeaders: string[];
  data: unknown[][];
  emailCount: number;
  filledRows: unknown[][];
  hasEmail: boolean;
  headers: string[];
  rowCount: number;
  validRows: unknown[][];
}

interface InvitesSpreadsheetInputSignature {
  Args: {
    disabled?: boolean;
    enablePropertyNotes?: boolean;
    flow?: FlowModel;
    isEmailRequired?: boolean;
    onChange?: (output: InvitesSpreadsheetOutputArgs) => void;
    onHotInstance?: (hotInstance: Handsontable) => void;
    onUpdatingChange?: (isUpdating: boolean) => void;
    options?: GridSettings;
    showRequiredOnly?: boolean;
    inviteTemplatesToCopy?: GroupInviteInstanceTemplate[];
  };
  Element: HTMLDivElement;
}

interface ColumnDefinition extends GridSettings {
  csvHeader?: string;
  dateFormat?: DateFieldFormat;
}

const STATIC_COL_HEADERS = Object.freeze(['Full name', 'Visitor email address']);

const validateEmail = function (email: string): boolean {
  return /^[^@\s]+@[^@.\s]+(?:(?:\.|:{1,2})[^@.:\s]+)+$/.test(email);
};

const createTextColumnWithGenericValidation = function (settings: ColumnDefinition): ColumnDefinition {
  const validator = function (value: string, cb: (valid: boolean) => void): void | RegExp {
    cb(settings.allowEmpty || value?.length > 0);
  };

  return {
    type: 'text',
    validator,
    ...settings,
  };
};

const createIntegerColumnWithPositiveValidation = function (settings: ColumnDefinition): ColumnDefinition {
  const validator = function (value: string, cb: (valid: boolean) => void): void | RegExp {
    const number = Number(value);

    cb(number >= 0 && Math.floor(number) === number);
  };

  return {
    ...settings,
    type: 'text',
    validator,
  };
};

const createStaticColumns = (isEmailRequired: boolean = false): ColumnDefinition[] => {
  return [
    createTextColumnWithGenericValidation({
      allowEmpty: false,
      csvHeader: 'invitee_name', // eslint-disable-line camelcase
      data: 'fullName',
    }),
    {
      allowEmpty: !isEmailRequired,
      csvHeader: 'invitee_email', // eslint-disable-line camelcase
      data: 'email',
      type: 'text',
      trimWhitespace: true,
      validator(val: string, cb: (valid: boolean) => void): void | RegExp {
        if (isBlank(val)) {
          cb(isEmailRequired ? false : true);
        } else {
          cb(validateEmail(val));
        }
      },
    },
  ];
};

export default class InvitesSpreadsheetInput extends Component<InvitesSpreadsheetInputSignature> {
  @tracked hotInstance!: Handsontable;
  @tracked rowLength: number = 0;

  @reads('args.flow') selectedFlow!: FlowModel;
  @reads('args.showRequiredOnly', false) showRequiredOnly!: boolean;
  @reads('selectedFlow.signInFieldPage.signInFields') signInFields!: AsyncHasMany<SignInFieldModel>;
  @reads('selectedFlow.additionalGuests', false) additionalGuests!: boolean;
  @filterBy('sortedSignInFields', 'isHighPriorityField') highPrioritySignInFields!: SignInFieldModel[];
  @filterBy('sortedSignInFields', 'isHighPriorityField', false) lowPrioritySignInFields!: SignInFieldModel[];

  get colHeaders(): string[] {
    const colHeaders = [...STATIC_COL_HEADERS];
    const {
      args: { enablePropertyNotes },
      highPrioritySignInFields,
      lowPrioritySignInFields,
      showRequiredOnly,
      additionalGuests,
    } = this;

    highPrioritySignInFields.forEach((field) => {
      const h = this.headingForField(field);

      if (!isBlank(h)) colHeaders.push(h);
    });

    if (additionalGuests && !showRequiredOnly) {
      colHeaders.push('Additional visitors');
    }

    lowPrioritySignInFields.forEach((field) => {
      const h = this.headingForField(field);

      if (!isBlank(h)) colHeaders.push(h);
    });

    if (!showRequiredOnly) {
      colHeaders.push('Private notes');

      if (enablePropertyNotes) {
        colHeaders.push('Shared notes');
      }
    }

    return colHeaders.map((h) => escapeHTML(h));
  }

  get prefilledBody(): unknown[] {
    const { inviteTemplatesToCopy } = this.args;
    const { colHeaders } = this;

    return (
      inviteTemplatesToCopy?.map((template) => {
        return {
          fullName: template.fullName,
          email: template.email,
          additionalGuests: template.additionalGuests,
          privateNotes: template.privateNotes,
          propertyNotes: template.propertyNotes,
          ...Object.fromEntries(
            Object.entries(template.userData ?? {}).filter(([key]) => colHeaders.includes(escapeHTML(key))),
          ),
        };
      }) ?? []
    );
  }

  get columns(): ColumnDefinition[] {
    const {
      args: { enablePropertyNotes, disabled },
      highPrioritySignInFields,
      lowPrioritySignInFields,
      showRequiredOnly,
      additionalGuests,
    } = this;
    const columns = [...createStaticColumns(this.args.isEmailRequired)];

    highPrioritySignInFields.forEach((field) => {
      const c = this.cellForField(field);

      if (c) columns.push(c);
    });

    if (additionalGuests && !showRequiredOnly) {
      columns.push(
        createIntegerColumnWithPositiveValidation({
          allowEmpty: true,
          csvHeader: 'additional_guests',
          data: 'additionalGuests',
        }),
      );
    }

    lowPrioritySignInFields.forEach((field) => {
      const c = this.cellForField(field);

      if (c) columns.push(c);
    });

    if (!showRequiredOnly) {
      columns.push({
        allowEmpty: true,
        csvHeader: 'private_notes',
        data: 'privateNotes',
        type: 'text',
      });

      if (enablePropertyNotes) {
        columns.push({
          allowEmpty: true,
          csvHeader: 'property_notes',
          data: 'propertyNotes',
          type: 'text',
        });
      }
    }

    return columns.map((col) => {
      col.readOnly = disabled;

      return col;
    });
  }

  get spreadsheetClassNames(): string {
    return this.columns
      .map((col, i) => {
        return col.allowEmpty ? `col-optional-${i}` : `col-required-${i}`;
      })
      .join(' ');
  }

  get sortedSignInFields(): SignInFieldModel[] {
    const { showRequiredOnly, signInFields } = this;
    const resolved = signInFields?.content?.slice();
    const results = A(
      A(resolved)
        .rejectBy('identifier', 'name')
        .filter((f) => f.isTopLevel !== false),
    ).sortBy('fieldPriority', 'position');

    return showRequiredOnly ? results.filterBy('isRequestedFromEmployee') : results;
  }

  headingForField(field: SignInFieldModel): string {
    // Set const individually to avoid "Unsafe array destructuring of a
    // tuple element with an `any` value" lint error
    const name = field?.name ?? '';
    const localized = field?.localized ?? '';
    const isEmail = field?.isEmail ?? false;
    const isPhone = field?.isPhone ?? false;
    const isHost = field?.isHost ?? false;

    if (isHost) return ''; // required ? localized || name : `${localized || name} (optional)`;
    if (isEmail) return '';
    if (isPhone) return 'Visitor phone number';

    if (field.isDate) {
      const dateFormat = field.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
      const label = localized || name;
      return `${label} (${dateFormat})`;
    }

    return localized || name;
  }

  cellForField(field: SignInFieldModel): ColumnDefinition | null {
    // Set const individually to avoid "Unsafe array destructuring of a
    // tuple element with an `any` value" lint error
    const name = field?.name ?? '';
    const isRequestedFromEmployee = field?.isRequestedFromEmployee ?? false;
    const kind = field?.kind ?? 'text';
    const isEmail = field?.isEmail ?? false;
    const isHost = field?.isHost ?? false;

    const opts = field.options;
    let options: string[] = [];

    if (opts.length && opts[0] instanceof DropdownOption) {
      options = opts.mapBy('value');
    }

    if (isHost) return null;
    if (isEmail) return null;

    if (field.isDate) {
      const dateFormat = field.dateFormat ?? DateFieldFormat.MM_DD_YYYY;
      const allowEmpty = !isRequestedFromEmployee;
      return {
        allowEmpty,
        correctFormat: true,
        csvHeader: name,
        data: escapeHTML(name),
        dateFormat,
        type: 'date',
        validator: createDateFieldValidator(dateFormat, allowEmpty),
      };
    }

    // Check if this field should be required based on parent field values
    const deps: ConditionalDependency[] = extractConditionalDependencies(field, escapeHTML);

    if (deps.length > 0) {
      return {
        allowEmpty: false, // Let the validator control when field is required keeps red dot present
        csvHeader: name,
        data: escapeHTML(name),
        source: kind === SignInFieldKind.SINGLE_SELECTION ? options : [],
        type: kind === SignInFieldKind.SINGLE_SELECTION ? 'dropdown' : 'text',
        validator: createConditionalPresenceValidator(deps),
      } as ColumnDefinition;
    }

    return createTextColumnWithGenericValidation({
      allowEmpty: !isRequestedFromEmployee,
      csvHeader: name,
      data: escapeHTML(name),
      source: kind === SignInFieldKind.SINGLE_SELECTION ? options : [],
      type: kind === SignInFieldKind.SINGLE_SELECTION ? 'dropdown' : 'text',
    });
  }

  /**
   * Strip invisible Unicode characters from clipboard data before paste.
   * Excel and Google Sheets embed zero-width spaces, BOM, and NBSP characters
   * that are invisible but cause backend dropdown validation to fail (SQ-5842).
   * Replaces with space (not empty string) to avoid conjoining words/names.
   */
  @action
  sanitizePasteData(_hotInstance: Handsontable, data: string[][]): void {
    const invisibleChars = /\u200B|\u200C|\u200D|\u2060|\uFEFF|\u00A0/g;
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[i]!.length; j++) {
        const val = data[i]![j];
        if (typeof val === 'string') {
          data[i]![j] = val.replace(invisibleChars, ' ').replace(/\s+/g, ' ').trim();
        }
      }
    }
  }

  @action
  setHotInstance(hotInstance: Handsontable): void {
    this.hotInstance = hotInstance;
    // if there is prefilled data, validate it once on init
    if (this.prefilledBody.length) void this.onRowUpdateTask.perform(hotInstance);
    this.args.onHotInstance?.(hotInstance);
  }

  onRowUpdateTask = task({ restartable: true }, async (hotInstance: Handsontable): Promise<void> => {
    const runId = this.onRowUpdateTask.performCount;
    this.args.onUpdatingChange?.(true);
    try {
      await timeout(zft(1));

      const data = <unknown[][]>hotInstance.getData();
      const validRows: unknown[][] = [];
      const filledRows: unknown[][] = [];
      const emailColumnIndex = this.columns.findIndex((c) => c.csvHeader === 'invitee_email');
      const emails: string[] = [];
      const headers = this.colHeaders;
      const csvHeaders = A(this.columns).mapBy('csvHeader') as string[];

      await Promise.all(
        data
          .map((row: unknown[], i: number) => {
            if (hotInstance.isEmptyRow(i)) return;

            filledRows.push(row);

            const email = (row[emailColumnIndex] ?? '') as string;

            if (validateEmail(email)) emails.push(email);

            return new Promise((resolve) => {
              hotInstance.validateRows([i], (valid) => {
                if (valid) validRows.push(row);
                resolve(valid);
              });
            });
          })
          .filter((promise) => promise !== undefined),
      );

      this.rowLength = filledRows.length;

      // Convert date field values in valid rows to ISO format for backend
      const dateColumns = this.columns
        .map((col, index) => (col.dateFormat ? { index, format: col.dateFormat } : null))
        .filter((item): item is { index: number; format: DateFieldFormat } => item !== null);

      if (dateColumns.length > 0) {
        for (let i = 0; i < validRows.length; i++) {
          const rowCopy = [...validRows[i]!];
          for (const { index, format } of dateColumns) {
            const value = rowCopy[index] as string;
            if (value) {
              rowCopy[index] = convertToISO(value, format) ?? value;
            }
          }
          validRows[i] = rowCopy;
        }
      }

      const hasEmail = this.rowLength > 0 && emails.length > 0;
      const output: InvitesSpreadsheetOutputArgs = {
        csvHeaders,
        data,
        emailCount: emails.length,
        filledRows,
        hasEmail,
        headers,
        rowCount: this.rowLength,
        validRows,
      };

      this.args.onChange?.(output);
    } finally {
      if (this.onRowUpdateTask.performCount === runId) {
        this.args.onUpdatingChange?.(false);
      }
    }
  });

  <template>
    <div
      ...attributes
      class="handsontable-input {{this.spreadsheetClassNames}}"
      data-test-invites-spreadsheet-input
      data-test-invites-spreadsheet-updating={{this.onRowUpdateTask.isRunning}}
    >
      <HandsonTable
        @hooks={{hash
          afterChange=this.onRowUpdateTask.perform
          afterRemoveRow=this.onRowUpdateTask.perform
          beforePaste=this.sanitizePasteData
        }}
        @options={{@options}}
        @data={{this.prefilledBody}}
        @colHeaders={{this.colHeaders}}
        @columns={{this.columns}}
        @registerInstance={{this.setHotInstance}}
      />
    </div>
  </template>
}
